/**
 * Zugriff auf den Server.
 *
 * Zwei Dinge sind hier wichtig: Das Sitzungscookie muss mitgeschickt werden, und bei verändernden
 * Anfragen prüft der Server die Herkunft. Beides erledigt `fetch` von selbst, solange die
 * Oberfläche unter derselben Adresse läuft – im Entwicklungsbetrieb sorgt der Umweg in der
 * Vite-Konfiguration dafür.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, pfad: string, körper?: unknown): Promise<T> {
  const antwort = await fetch(pfad, {
    method,
    credentials: 'same-origin',
    headers: körper === undefined ? {} : { 'content-type': 'application/json' },
    ...(körper === undefined ? {} : { body: JSON.stringify(körper) }),
  });

  if (antwort.status === 204) return undefined as T;

  const daten = await antwort.json().catch(() => ({}));
  if (!antwort.ok) {
    throw new ApiError(
      (daten as { code?: string }).code ?? 'ERROR',
      (daten as { message?: string }).message ?? 'Da ist etwas schiefgegangen.',
      antwort.status,
    );
  }
  return daten as T;
}

export type BuiltinCommand = {
  trigger: string;
  enabled: boolean;
  cooldownSeconds: number;
  permission: 'EVERYONE' | 'VIP' | 'MOD' | 'BROADCASTER';
  response: string | null;
};

export type ChatCommand = {
  id: string;
  lobbyId: string;
  trigger: string;
  response: string;
  cooldownSeconds: number;
  enabled: boolean;
  permission: 'EVERYONE' | 'VIP' | 'MOD' | 'BROADCASTER';
};

export type ChatCommandInput = {
  trigger: string;
  response: string;
  cooldownSeconds: number;
  enabled: boolean;
  permission: 'EVERYONE' | 'VIP' | 'MOD' | 'BROADCASTER';
};

export const api = {
  // --- SirBotrick: Spielbefehle ---
  builtinCommands: (lobbyId: string) => request<BuiltinCommand[]>('GET', `/api/${lobbyId}/commands/builtin`),
  builtinCommandSave: (lobbyId: string, trigger: string, data: Partial<Omit<BuiltinCommand,'trigger'>>) => request<BuiltinCommand>('PUT', `/api/${lobbyId}/commands/builtin/${trigger}`, data),
  builtinCommandReset: (lobbyId: string, trigger: string) => request<{ok:true}>('DELETE', `/api/${lobbyId}/commands/builtin/${trigger}`),
  // --- SirBotrick: lobbyindividuelle Commands ---
  chatCommands: (lobbyId: string) =>
    request<ChatCommand[]>('GET', `/api/${lobbyId}/commands`),

  chatCommandAnlegen: (lobbyId: string, data: ChatCommandInput) =>
    request<ChatCommand>('POST', `/api/${lobbyId}/commands`, data),

  chatCommandÄndern: (
    lobbyId: string,
    commandId: string,
    data: Partial<ChatCommandInput>,
  ) =>
    request<ChatCommand>('PATCH', `/api/${lobbyId}/commands/${commandId}`, data),

  chatCommandLöschen: (lobbyId: string, commandId: string) =>
    request<{ ok: true }>('DELETE', `/api/${lobbyId}/commands/${commandId}`),

  winChallengeSettings: (lobbyId: string, enabled: boolean, points: number) => request<{ok:true}>('PUT', `/api/${lobbyId}/win/challenge-settings`, { enabled, points }),
  winChallengeResults: (lobbyId: string) => request<{ runs: {id:string;state:string;openedAt:string}[]; results: {runId:string;teamId:string;passed:boolean}[] }>('GET', `/api/${lobbyId}/win/challenge-results`),
  winChallengeResult: (lobbyId: string, runId: string, teamId: string, passed: boolean) => request<unknown>('PUT', `/api/${lobbyId}/win/challenge-results/${runId}/${teamId}`, { passed }),
  winMatches: (lobbyId: string) => request<import('../views/WinPunktetafel').WinStand>('GET', `/api/${lobbyId}/win/matches`),
  winMatchAnlegen: (lobbyId: string, data: { title: string; detail: string; points: number }) => request<unknown>('POST', `/api/${lobbyId}/win/matches`, data),
  winMatchÄndern: (lobbyId: string, matchId: string, data: { title?: string; detail?: string; points?: number; winnerTeamId?: string | null }) => request<unknown>('PATCH', `/api/${lobbyId}/win/matches/${matchId}`, data),
  winMatchLöschen: (lobbyId: string, matchId: string) => request<unknown>('DELETE', `/api/${lobbyId}/win/matches/${matchId}`),

  login: (username: string, password: string) =>
    request<{ mfaRequired: boolean }>('POST', '/api/auth/login', { username, password }),
  verifyMfa: (code: string) => request<{ ok: true }>('POST', '/api/auth/mfa', { code }),
  logout: () => request<{ ok: true }>('POST', '/api/auth/logout'),
  me: () => request<import('./types').Me>('GET', '/api/auth/me'),
  passwortÄndern: (currentPassword: string, newPassword: string) => request<{ ok: true }>('POST', '/api/auth/change-password', { currentPassword, newPassword }),

  lobbies: () => request<import('./types').LobbyZeile[]>('GET', '/api/lobbies'),
  lobby: (lobbyId: string) => request<import('./types').LiveState>('GET', `/api/lobbies/${lobbyId}`),
  createLobby: (eingabe: Record<string, unknown>) =>
    request<{ id: string; name: string; overlayKey: string }>('POST', '/api/lobbies', eingabe),
  updateLobby: (lobbyId: string, eingabe: Record<string, unknown>) =>
    request<unknown>('PATCH', `/api/lobbies/${lobbyId}`, eingabe),
  rotateOverlayKey: (lobbyId: string) =>
    request<{ overlayKey: string }>('POST', `/api/lobbies/${lobbyId}/overlay-key`, {}),

  addTeam: (lobbyId: string, eingabe: { name: string; color: string }) =>
    request<{ id: string }>('POST', `/api/lobbies/${lobbyId}/teams`, eingabe),
  updateTeam: (lobbyId: string, teamId: string, eingabe: Record<string, unknown>) =>
    request<unknown>('PATCH', `/api/lobbies/${lobbyId}/teams/${teamId}`, eingabe),
  removeTeam: (lobbyId: string, teamId: string) =>
    request<{ ok: true }>('DELETE', `/api/lobbies/${lobbyId}/teams/${teamId}`),

  tasks: (lobbyId: string) =>
    request<import('./types').Task[]>('GET', `/api/lobbies/${lobbyId}/tasks`),
  addTask: (lobbyId: string, eingabe: { title: string; description?: string }) =>
    request<import('./types').Task>('POST', `/api/lobbies/${lobbyId}/tasks`, eingabe),
  updateTask: (lobbyId: string, taskId: string, eingabe: Record<string, unknown>) =>
    request<import('./types').Task>('PATCH', `/api/lobbies/${lobbyId}/tasks/${taskId}`, eingabe),
  removeTask: (lobbyId: string, taskId: string) =>
    request<{ ok: true }>('DELETE', `/api/lobbies/${lobbyId}/tasks/${taskId}`),
  importTasks: (lobbyId: string, aufgaben: { title: string; description?: string }[]) =>
    request<{ imported: number }>('POST', `/api/lobbies/${lobbyId}/tasks/import`, aufgaben),

  addMember: (lobbyId: string, eingabe: { userId: string; teamIds?: string[] }) =>
    request<{ ok: true }>('POST', `/api/lobbies/${lobbyId}/members`, eingabe),
  inviteMember: (lobbyId: string, eingabe: { username: string; teamIds?: string[] }) =>
    request<import('./types').Einladung>('POST', `/api/lobbies/${lobbyId}/members/invite`, eingabe),
  editMember: (lobbyId: string, userId: string, data: { displayName?: string; username?: string; teamIds?: string[] }) =>
    request<{ ok: true }>('PATCH', `/api/lobbies/${lobbyId}/members/${userId}`, data),
  teamChatVisibility: (lobbyId: string, teamId: string, userId: string, visible: boolean) =>
    request<{ ok: true }>('PUT', `/api/lobbies/${lobbyId}/teams/${teamId}/members/${userId}/chat-visibility`, { visible }),
  resetMemberPassword: (lobbyId: string, userId: string) =>
    request<{ password: string }>('POST', `/api/lobbies/${lobbyId}/members/${userId}/password-reset`, {}),
  removeMember: (lobbyId: string, userId: string) =>
    request<{ ok: true }>('DELETE', `/api/lobbies/${lobbyId}/members/${userId}`),

  // --- Proben in Testlobbys ---
  probeKanäle: (lobbyId: string) =>
    request<{ angelegt: number }>('POST', `/api/lobbies/${lobbyId}/probe/kanaele`, {}),
  probeStimmen: (lobbyId: string, anzahl: number) =>
    request<{ abgegeben: number }>('POST', `/api/lobbies/${lobbyId}/probe/stimmen`, { anzahl }),
  probeFelder: (lobbyId: string, anzahl: number) =>
    request<{ belegt: number }>('POST', `/api/lobbies/${lobbyId}/probe/felder`, { anzahl }),
  probeAufräumen: (lobbyId: string) =>
    request<{ ok: true }>('POST', `/api/lobbies/${lobbyId}/probe/aufraeumen`, {}),

  botStatus: () => request<{ connected: boolean; login: string | null; expiresAt: string | null }>('GET', '/api/admin/bot/status'),
  botAuthorize: () => request<{ url: string }>('POST', '/api/admin/bot/authorize', {}),
  botDisconnect: () => request<{ ok: true }>('DELETE', '/api/admin/bot'),

  // --- Verwaltung ---
  systemstand: () => request<import('./types').Systemstand>('GET', '/api/admin/status'),
  protokoll: (filter: Record<string, string | number | undefined> = {}) => {
    const parameter = new URLSearchParams();
    for (const [name, wert] of Object.entries(filter)) {
      if (wert !== undefined && wert !== '') parameter.set(name, String(wert));
    }
    const anhang = parameter.toString();
    return request<import('./types').Protokollzeile[]>(
      'GET',
      `/api/admin/log${anhang ? `?${anhang}` : ''}`,
    );
  },
  protokollArten: () => request<string[]>('GET', '/api/admin/log/arten'),
  konten: () => request<import('./types').Konto[]>('GET', '/api/admin/users'),
  beendeSitzungen: (userId: string) =>
    request<{ beendet: number }>('POST', `/api/admin/users/${userId}/sessions/revoke`, {}),
  setzeGesperrt: (userId: string, gesperrt: boolean) =>
    request<{ ok: true }>('POST', `/api/admin/users/${userId}/disabled`, { gesperrt }),

  meineTwitchKanäle: (lobbyId: string) =>
    request<import('./types').TwitchKanal[]>('GET', `/api/lobbies/${lobbyId}/twitch/me`),
  meinenTwitchKanalTrennen: (lobbyId: string, channelId: string) =>
    request<{ ok: true }>('DELETE', `/api/lobbies/${lobbyId}/twitch/me/${channelId}`),

  twitchKanäle: (lobbyId: string) =>
    request<import('./types').TwitchKanal[]>('GET', `/api/lobbies/${lobbyId}/twitch`),
  twitchVerbinden: (lobbyId: string) =>
    request<{ url: string }>('POST', `/api/lobbies/${lobbyId}/twitch/authorize`, {}),
  twitchTeam: (lobbyId: string, channelId: string, teamId: string | null) =>
    request<{ ok: true }>('PATCH', `/api/lobbies/${lobbyId}/twitch/${channelId}`, { teamId }),
  twitchTrennen: (lobbyId: string, channelId: string) =>
    request<{ ok: true }>('DELETE', `/api/lobbies/${lobbyId}/twitch/${channelId}`),

  pollSettings: (lobbyId: string) =>
    request<{ firstDelayMinutes: number; repeatMinutes: number; cutoffMinutes: number; automaticEnabled: boolean; announcement: string; nextAt: string | null }>(
      'GET', `/api/lobbies/${lobbyId}/polls/settings`),
  savePollSettings: (lobbyId: string, settings: {
    firstDelayMinutes: number; repeatMinutes: number; cutoffMinutes: number; announcement: string;
  }) => request<{ firstDelayMinutes: number; repeatMinutes: number; cutoffMinutes: number; announcement: string }>(
    'PUT', `/api/lobbies/${lobbyId}/polls/settings`, settings),

  setAutomaticPolls: (lobbyId: string, enabled: boolean) =>
    request<{ automaticEnabled: boolean; nextAt: string | null }>(
      'PATCH', `/api/lobbies/${lobbyId}/polls/automatic`, { enabled }),

  questions: (lobbyId: string) =>
    request<import('./types').PollFrage[]>('GET', `/api/lobbies/${lobbyId}/questions`),
  addQuestion: (lobbyId: string, eingabe: Record<string, unknown>) =>
    request<import('./types').PollFrage>('POST', `/api/lobbies/${lobbyId}/questions`, eingabe),
  updateQuestion: (lobbyId: string, questionId: string, eingabe: Record<string, unknown>) =>
    request<import('./types').PollFrage>(
      'PATCH',
      `/api/lobbies/${lobbyId}/questions/${questionId}`,
      eingabe,
    ),
  removeQuestion: (lobbyId: string, questionId: string) =>
    request<{ ok: true }>('DELETE', `/api/lobbies/${lobbyId}/questions/${questionId}`),

  openPoll: (lobbyId: string, questionId: string) =>
    request<{ runId: string; closesAt: string }>('POST', `/api/lobbies/${lobbyId}/polls`, {
      questionId,
    }),
  closePoll: (lobbyId: string) =>
    request<unknown>('POST', `/api/lobbies/${lobbyId}/polls/close`, {}),
  cancelPoll: (lobbyId: string) =>
    request<{ ok: true }>('POST', `/api/lobbies/${lobbyId}/polls/cancel`, {}),

  resetRound: (lobbyId: string) =>
    request<{ roundId: string }>('POST', `/api/lobbies/${lobbyId}/rounds/reset`, {}),
  startRound: (lobbyId: string) =>
    request<{ roundId: string; boards: number }>('POST', `/api/lobbies/${lobbyId}/rounds`, {}),
  toggleField: (lobbyId: string, teamId: string, cellIndex: number) =>
    request<{ action: 'CLAIMED' | 'RELEASED' }>('POST', `/api/lobbies/${lobbyId}/fields`, {
      teamId,
      cellIndex,
    }),
  timer: (lobbyId: string, action: string, minutes?: number) =>
    request<unknown>('POST', `/api/lobbies/${lobbyId}/timer`, {
      action,
      ...(minutes === undefined ? {} : { minutes }),
    }),
};
